import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { api } from "../services/api";

function getSection(page, sectionKey) {
  return (page?.sections || []).find((section) => section.key === sectionKey);
}

function getField(page, sectionKey, fieldKey) {
  const section = getSection(page, sectionKey);
  return (section?.fields || []).find((field) => field.key === fieldKey);
}

function parseJson(value, fallback) {
  if (Array.isArray(value) || (value && typeof value === "object")) return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export function useCmsPage(slug, fallback = {}) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const location = useLocation();
  const { isAdmin } = useAuth();

  const editMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return isAdmin && params.get("cmsEdit") === "1";
  }, [isAdmin, location.search]);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError("");

    api
      .get(`/cms/pages/${slug}`)
      .then((data) => {
        if (active) setPage(data.page || null);
      })
      .catch((err) => {
        if (active) setError(err.message || "CMS content unavailable");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [slug]);

  const text = useCallback(
    (sectionKey, fieldKey, fallbackValue = "") => {
      const field = getField(page, sectionKey, fieldKey);
      return field?.value ?? fallback?.[sectionKey]?.[fieldKey] ?? fallbackValue;
    },
    [fallback, page]
  );

  const json = useCallback(
    (sectionKey, fieldKey, fallbackValue = []) => parseJson(text(sectionKey, fieldKey, ""), fallbackValue),
    [text]
  );

  const updateField = useCallback(
    async (sectionKey, fieldKey, value) => {
      const saveId = `${sectionKey}.${fieldKey}`;
      setSavingKey(saveId);
      const data = await api.patch(`/cms/admin/pages/${slug}/fields`, { sectionKey, fieldKey, value });
      setPage(data.page || null);
      setSavingKey("");
      return data.page;
    },
    [slug]
  );

  return {
    editMode,
    error,
    json,
    loading,
    page,
    savingKey,
    text,
    updateField,
  };
}
